/**
 * Validation utilities for the Group Booking System.
 * Each function validates a specific field according to the booking data structure requirements.
 */

/**
 * Validates a phone number in international format.
 * Format: '+' followed by 7-15 digits, spaces allowed as separators.
 * @param {string} phone - The phone number to validate
 * @returns {boolean} True if the phone number is valid
 */
export function validatePhone(phone) {
  if (typeof phone !== 'string') return false;
  const cleaned = phone.replace(/\s/g, '');
  return /^\+\d{7,15}$/.test(cleaned);
}

/**
 * Validates the number of participants.
 * Must be an integer between 1 and 200 (inclusive).
 * @param {number|string} count - The participant count to validate
 * @returns {boolean} True if the count is valid
 */
export function validateParticipants(count) {
  if (typeof count === 'number' && !Number.isInteger(count)) return false;
  const n = parseInt(count, 10);
  return Number.isInteger(n) && n >= 1 && n <= 200;
}

/**
 * Validates a booking reference.
 * Must be alphanumeric and at most 20 characters.
 * @param {string} ref - The booking reference to validate
 * @returns {boolean} True if the reference is valid
 */
export function validateBookingReference(ref) {
  if (typeof ref !== 'string') return false;
  return /^[A-Za-z0-9]{1,20}$/.test(ref);
}

/**
 * Validates a meeting point name.
 * Must be a string between 1 and 60 characters after trimming.
 * @param {string} name - The meeting point name to validate
 * @returns {boolean} True if the name is valid
 */
export function validateMeetingPointName(name) {
  if (typeof name !== 'string') return false;
  const trimmed = name.trim();
  return trimmed.length >= 1 && trimmed.length <= 60;
}

/**
 * Validates that required booking fields are non-empty.
 * Required fields: first_name, participants, language.
 * @param {object} booking - The booking object to validate
 * @returns {boolean} True if all required fields are present and non-empty
 */
export function validateRequiredFields(booking) {
  if (!booking || typeof booking !== 'object') return false;
  return !!(
    booking.first_name?.trim() &&
    booking.participants &&
    booking.language?.trim()
  );
}

/**
 * Validates an optional amount field.
 * If provided, must be an integer between 1 and 999,999.
 * Returns true if the amount is empty/null/undefined (optional field).
 * @param {number|string|null|undefined} amount - The amount to validate
 * @returns {boolean} True if the amount is valid or not provided
 */
export function validateAmount(amount) {
  if (amount === null || amount === undefined || amount === '') return true;
  const n = parseInt(amount, 10);
  return Number.isInteger(n) && n >= 1 && n <= 999999;
}

/**
 * Validates a booking option quantity.
 * Must be an integer between 1 and 10 (inclusive).
 * @param {number|string} qty - The quantity to validate
 * @returns {boolean} True if the quantity is valid
 */
export function validateOptionQuantity(qty) {
  if (typeof qty === 'number' && !Number.isInteger(qty)) return false;
  const n = parseInt(qty, 10);
  return Number.isInteger(n) && n >= 1 && n <= 10;
}
